import { isValidElement, ReactNode } from 'react';
import { Form as AntForm, Button, FormItemProps, FormProps, theme } from 'antd';

import { PlatformAlert } from '@/shared/components/alerts';
import { BaseSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';

interface SmallLabelElementProps {
    children?: ReactNode;
}

export interface PlatformFormProps<Values = Record<string, unknown>> extends FormProps<Values> {
    error?: Error | ReactNode;
    errorTitle?: ReactNode;
    loading?: boolean;
    loadingFields?: number;
    onRetry?: () => void;
}

export interface PlatformFormItemProps<Values = Record<string, unknown>> extends FormItemProps<Values> {
    description?: ReactNode;
}

interface PlatformFormSkeletonProps {
    fields?: number;
}

const unwrapLegacySmallLabel = (label: ReactNode): ReactNode => {
    if (isValidElement<SmallLabelElementProps>(label) && label.type === 'small') {
        return label.props.children;
    }

    return label;
};

export const PlatformFormSkeleton = ({ fields = 3 }: PlatformFormSkeletonProps): JSX.Element => {
    const { token } = theme.useToken();

    return (
        <div aria-busy='true' aria-label='Loading form' role='status'>
            {Array.from({ length: fields }, (_, index) => (
                <div key={index} style={{ marginBottom: token.marginSM }}>
                    <BaseSkeleton paragraph={false} title={{ width: '28%' }} />
                    <ButtonSkeleton
                        block
                        shape='square'
                        style={{ display: 'block', height: token.controlHeight, marginTop: token.marginXXS }}
                    />
                </div>
            ))}
        </div>
    );
};

const PlatformFormItemRoot = <Values = Record<string, unknown>,>({
    description,
    extra,
    label,
    ...props
}: PlatformFormItemProps<Values>): JSX.Element => {
    const { token } = theme.useToken();
    const normalizedLabel = label ? (
        <span
            style={{
                color: token.colorTextTertiary,
                fontSize: token.fontSizeSM,
                lineHeight: token.lineHeightSM,
            }}
        >
            {unwrapLegacySmallLabel(label)}
        </span>
    ) : (
        label
    );
    const normalizedExtra =
        extra ??
        (description ? (
            <small style={{ color: token.colorTextTertiary, display: 'block' }}>{description}</small>
        ) : undefined);

    return <AntForm.Item {...props} extra={normalizedExtra} label={normalizedLabel} />;
};

const PlatformFormItem = Object.assign(PlatformFormItemRoot, {
    useStatus: AntForm.Item.useStatus,
});

const PlatformFormRoot = <Values = Record<string, unknown>,>({
    children,
    colon = false,
    error,
    errorTitle = 'Unable to load this form',
    layout = 'vertical',
    loading = false,
    loadingFields = 3,
    onRetry,
    requiredMark = false,
    scrollToFirstError = { behavior: 'smooth', block: 'center' },
    validateTrigger = 'onBlur',
    ...props
}: PlatformFormProps<Values>): JSX.Element => {
    const errorDescription = error instanceof Error ? error.message : error;

    return (
        <AntForm<Values>
            {...props}
            colon={colon}
            layout={layout}
            requiredMark={requiredMark}
            scrollToFirstError={scrollToFirstError}
            validateTrigger={validateTrigger}
        >
            {
                (loading ? (
                    <PlatformFormSkeleton fields={loadingFields} />
                ) : error ? (
                    <PlatformAlert
                        action={
                            onRetry ? (
                                <Button onClick={onRetry} size='small'>
                                    Retry
                                </Button>
                            ) : undefined
                        }
                        description={errorDescription}
                        message={errorTitle}
                        showIcon
                        type='error'
                    />
                ) : (
                    children
                )) as ReactNode
            }
        </AntForm>
    );
};

const PlatformForm = Object.assign(PlatformFormRoot, {
    ErrorList: AntForm.ErrorList,
    Item: PlatformFormItem,
    List: AntForm.List,
    Provider: AntForm.Provider,
    create: AntForm.create,
    useForm: AntForm.useForm,
    useFormInstance: AntForm.useFormInstance,
    useWatch: AntForm.useWatch,
});

export default PlatformForm;
